import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DataserviceService } from '../dataservice.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-my-pay-history',
  templateUrl: './my-pay-history.component.html',
  styleUrl: './my-pay-history.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyPayHistoryComponent implements OnInit, OnDestroy {

  constructor(
    private dataService: DataserviceService,
    private cdr: ChangeDetectorRef,
    private message: NzMessageService,
    private modal: NzModalService
  ) { }

  customerPerformanceModal: boolean = false;
  selectedCustomerPerformance: any;
  selectedCustomerAddressLines: string[] = [];

  currentUser: any;
  payHistory: any[] = [];
  visiblePayHistory: any[] = [];
  allGroupedPayHistory: any[] = [];
  groupedPayHistory: any[] = [];
  adjustments: any[] = [];
  expandSet = new Set<string>();
  test: any = ["test"];
  upcoming: any[] = [];
  verbiage: string = '';
  cycleBatchSize = 5;
  cycleOffset = 0;
  totalCycleCount = 0;
  loadingMoreCycles = false;
  loadedCycleCount = 0;

  // New properties for cycle selection and customer details
  selectedCycleId: string = '';
  selectedCycleCustomers: any[] = [];
  customerDetailsLoading = false;
  loading = false;
  private loadingTipTimeout: ReturnType<typeof setTimeout> | null = null;

  // Mobile card expansion state
  expandedCycleCards = new Set<string>();
  expandedCustomerCards = new Set<string>();

  // KPI metrics
  lifetimeNet = 0;
  ytdNet = 0;
  lastPeriodChangePct: number | null = null;
  threePeriodAvg = 0;
  salesCountTotal = 0;
  latestPayoutAmount = 0;
  latestPayoutDate: Date | null = null;
  latestCommissionAmount = 0;
  latestAdjustmentAmount = 0;
  latestHoldbackAmount = 0;

  // Filters
  dateRange: Date[] = [];
  searchText: string = '';
  associations: any[] = [];
  selectedUserId: string | null = null;
  ownerId: string | null = null;

  // Charts
  trendChartData: any;
  trendChartOptions: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true } }, scales: { y: { beginAtZero: true } } };
  donutChartData: any;
  donutChartOptions: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } };
  upcomingChartData: any;
  upcomingChartOptions: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } };

  // Motivational
  motivationalMessage: string = '';

  // Computed helpers
  get upcomingTotal(): number {
    try {
      return (this.upcoming || []).reduce((sum: number, u: any) => sum + Number(u?.amount || 0), 0);
    } catch {
      return 0;
    }
  }

  get hasMoreCycles(): boolean {
    return this.loadedCycleCount < this.totalCycleCount;
  }

  ngOnInit() {
    this.getData();
  }

  getData(reset: boolean = true) {
    if (reset) {
      this.allGroupedPayHistory = [];
      this.groupedPayHistory = [];
      this.payHistory = [];
      this.cycleOffset = 0;
      this.loadedCycleCount = 0;
      this.totalCycleCount = 0;
      this.selectedCycleId = '';
      this.selectedCycleCustomers = [];
      this.expandedCycleCards.clear();
      this.expandedCustomerCards.clear();
      this.setLoadingState(true, 'Loading your pay history...');
    } else {
      this.loadingMoreCycles = true;
    }

    this.currentUser = JSON.parse(localStorage.getItem('user')!);

    // Use the individual payroll history endpoint with batching
    this.dataService.getIndividualPayrollHistory(
      this.selectedUserId || undefined,
      this.cycleBatchSize,
      this.cycleOffset
    )
      .subscribe({
        next: (response: any) => {
          this.associations = Array.isArray(response?.associations) ? response.associations : this.associations;
          this.ownerId = response?.owner_id || this.currentUser?.id || null;
          if (reset) {
            this.selectedUserId = response?.selected_user_id || this.ownerId;
          }
          const rawGroupsSource =
            [response, response?.data, response?.results, response?.commissions].find((value: any) => Array.isArray(value));
          const rawGroups = Array.isArray(rawGroupsSource) ? rawGroupsSource : [];
          if (!rawGroupsSource && response) {
            console.warn('Unexpected payroll history response shape', response);
          }

          if (response?.total_cycles !== undefined && response?.total_cycles !== null) {
            this.totalCycleCount = Number(response.total_cycles);
          } else if (reset) {
            this.totalCycleCount = rawGroups.length;
          }

          // Map grouped data for the UI and merge with any previously loaded cycles
          const mappedGroups = rawGroups.map((group: any) => {
            const userPayee = group.payees?.[0] || {};
            const customers = userPayee.customers || [];

            return {
              ...group,
              cycleId: group.cycles?.[0]?.id || group.id,
              customers: customers,
              totalCommissions: userPayee.totalCommissions || group.totalCommissions || 0,
              totalHoldbacks: userPayee.totalHoldbacks || group.totalHoldbacks || 0,
              totalAdjustments: userPayee.totalAdjustments || group.totalAdjustments || 0,
              netPay: group.netPay,
              totalSales: group.cycles?.[0].totalRecords
            };
          }).sort((a: any, b: any) => {
            if (!a.payDate && !b.payDate) return 0;
            if (!a.payDate) return 1;
            if (!b.payDate) return -1;
            return new Date(b.payDate).getTime() - new Date(a.payDate).getTime();
          });

          this.allGroupedPayHistory = this.mergeCycles(this.allGroupedPayHistory, mappedGroups);

          // For backward compatibility with metrics, create payHistory from grouped data
          this.payHistory = this.allGroupedPayHistory.map((group: any) => ({
            id: group.id,
            payDate: group.payDate ? new Date(group.payDate) : null,
            amount: group.netPay,
            sales: group.customers || [],
            holdbacks: (group.payees?.[0]?.holdbacks) || []
          }));

          // Set upcoming to adjustments (if available) otherwise leave empty
          this.upcoming = Array.isArray(response?.adjustments) ? response.adjustments : (reset ? [] : this.upcoming);

          this.applyFilters();
          this.computeMetrics();
          this.buildMotivation();
          const increment = Array.isArray(response?.returned_cycle_ids)
            ? response.returned_cycle_ids.length
            : rawGroups.length;
          this.cycleOffset = reset ? increment : (this.cycleOffset + increment);
          this.loadedCycleCount = Math.min(this.cycleOffset, this.totalCycleCount || this.cycleOffset);
          this.setLoadingState(false);
          this.loadingMoreCycles = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading payroll history', error);
          this.groupedPayHistory = [];
          this.payHistory = [];
          this.upcoming = [];
          this.setLoadingState(false);
          this.loadingMoreCycles = false;
        }
      });
  }

  onUserChange(userId: string) {
    this.selectedUserId = userId || this.ownerId;

    this.selectedCycleId = '';
    this.selectedCycleCustomers = [];
    this.selectedCustomerPerformance = null;
    this.selectedCustomerAddressLines = [];
    this.customerPerformanceModal = false;
    this.expandedCycleCards.clear();
    this.expandedCustomerCards.clear();
    this.customerDetailsLoading = false;
    this.cdr.markForCheck();
    this.getData(true);
  }

  loadMoreCycles(): void {
    if (this.loadingMoreCycles || !this.hasMoreCycles) {
      return;
    }
    this.getData(false);
  }

  onExpandChange(id: string, checked: boolean): void {
    if (checked) {
      this.expandSet.add(id);
    } else {
      this.expandSet.delete(id);
    }
  }

  // New methods for cycle selection and customer details
  loadCycleCustomers(cycleId: string): void {
    if (this.selectedCycleId === cycleId) {
      // Toggle off if already selected
      this.selectedCycleId = '';
      this.selectedCycleCustomers = [];
      return;
    }

    this.customerDetailsLoading = true;
    this.selectedCycleId = cycleId;

    // Find the cycle to get all the actual payroll cycle IDs
    const cycle = this.allGroupedPayHistory.find((c: any) => c.id === cycleId);
    if (!cycle) {
      this.selectedCycleId = '';
      this.selectedCycleCustomers = [];
      this.customerDetailsLoading = false;
      this.message.error('Unable to find that payroll cycle');
      return;
    }
    const allCycleIds = cycle?.cycles?.map((c: any) => c.id) || [cycle?.cycleId || cycleId];

    console.log(allCycleIds)
    // Use the new lazy loading endpoint to get detailed customer data
    this.dataService.getCycleCustomerDetails(allCycleIds, this.selectedUserId || this.currentUser.id)
      .subscribe({
        next: (customerDetails: any[]) => {
          // Map the API response to the expected format
          this.selectedCycleCustomers = customerDetails;

          this.customerDetailsLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading customer details:', error);
          this.customerDetailsLoading = false;
          this.selectedCycleId = '';
          this.selectedCycleCustomers = [];
          // this.cdr.markForCheck();
        }
      });
  }

  loadAdjustmentModal(r: any) {
    console.log('loadAdjustmentModal: ', r)
  }


  isCycleExpanded(cycleId: string): boolean {
    return this.selectedCycleId === cycleId;
  }

  trackByCycleId(index: number, cycle: any): string {
    return cycle.id;
  }

  trackByCustomerId(index: number, customer: any): string {
    return customer.customerId;
  }

  trackByAdjustment(index: number, adjustment: any): string {
    return adjustment?.id || adjustment?.adjustmentId || adjustment?.adjustment_id || adjustment?.payplan_adjustment_id || `${index}`;
  }

  asNumber(value: any): number {
    const coerced = Number(value);
    return Number.isFinite(coerced) ? coerced : 0;
  }


  applyFilters(): void {
    const hasRange = this.dateRange && this.dateRange.length === 2;
    const [start, end] = hasRange ? this.dateRange : [null, null] as any;
    const text = (this.searchText || '').toLowerCase();

    // Filter the grouped data directly
    const source = this.allGroupedPayHistory || [];
    this.groupedPayHistory = source.filter((group: any) => {
      const payDate = group.payDate ? new Date(group.payDate) : null;
      const withinRange = !hasRange || (!payDate ? false : (payDate >= start && payDate <= end));
      if (!withinRange) return false;
      if (!text) return true;

      // Search in group data and customer names
      const inGroup = [group.netPay, payDate?.toLocaleDateString?.()].join(' ').toLowerCase();
      const inCustomers = (group.customers || []).some((customer: any) => {
        const customerName = this.formatCustomerName(customer);
        const rule = customer.rule_description || '';
        return [customerName, rule, customer.amount].join(' ').toLowerCase().includes(text);
      });
      return inGroup.includes(text) || inCustomers;
    });
  }

  private mergeCycles(existing: any[], incoming: any[]): any[] {
    const merged = new Map<string, any>();
    [...existing, ...incoming].forEach((cycle: any) => {
      if (!cycle?.id) {
        return;
      }
      merged.set(cycle.id, cycle);
    });

    return Array.from(merged.values()).sort((a: any, b: any) => {
      if (!a.payDate && !b.payDate) return 0;
      if (!a.payDate) return 1;
      if (!b.payDate) return -1;
      return new Date(b.payDate).getTime() - new Date(a.payDate).getTime();
    });
  }


  computeMetrics(): void {
    // Use the grouped data directly for metrics calculations
    const payDateGroups = (this.payHistory || []).slice().sort((a: any, b: any) => {
      if (!a.payDate && !b.payDate) return 0;
      if (!a.payDate) return 1;
      if (!b.payDate) return -1;
      return b.payDate.getTime() - a.payDate.getTime();
    });

    this.lifetimeNet = payDateGroups.reduce((sum: number, group: any) => sum + Number(group.amount || 0), 0);
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    this.ytdNet = payDateGroups
      .filter((group: any) => group.payDate && new Date(group.payDate) >= startOfYear)
      .reduce((sum: number, group: any) => sum + Number(group.amount || 0), 0);

    if (payDateGroups.length >= 2) {
      const [current, previous] = [payDateGroups[0], payDateGroups[1]];
      const prevAmount = Number(previous.amount || 0);
      const currAmount = Number(current.amount || 0);
      this.lastPeriodChangePct = prevAmount === 0 ? null : ((currAmount - prevAmount) / prevAmount) * 100;
    } else {
      this.lastPeriodChangePct = null;
    }

    const lastThree = payDateGroups.slice(0, 3);
    this.threePeriodAvg = lastThree.length
      ? lastThree.reduce((sum: number, group: any) => sum + Number(group.amount || 0), 0) / lastThree.length
      : 0;

    this.salesCountTotal = payDateGroups.reduce((sum: number, group: any) => sum + (group.sales?.length || 0), 0);

    this.latestPayoutAmount = payDateGroups[0]?.amount || 0;
    this.latestPayoutDate = payDateGroups[0]?.payDate || null;
    if (payDateGroups[0]) {
      const allSales = payDateGroups[0].sales || [];
      this.latestAdjustmentAmount = allSales.filter((s: any) => s?.pay_type === 'commission_adjustment').reduce((sum: number, s: any) => sum + Number(s.amount || 0), 0);

      // Look for holdback data from the holdbacks array
      this.latestHoldbackAmount = 0;
      if (payDateGroups[0].holdbacks) {
        this.latestHoldbackAmount = payDateGroups[0].holdbacks.reduce((sum: number, h: any) => sum + Number(h.holdback_amount || 0), 0);
      }

      this.latestCommissionAmount = allSales
        .filter((s: any) => s?.pay_type !== 'commission_adjustment')
        .reduce((sum: number, s: any) => sum + Number(s.amount || 0), 0);
    } else {
      this.latestAdjustmentAmount = 0;
      this.latestCommissionAmount = 0;
      this.latestHoldbackAmount = 0;
    }

    // Build monthly trend (last 12 months) - using pay history data
    const months: string[] = [];
    const monthKeys: string[] = [];
    const monthFormat = (d: Date) => d.toLocaleString(undefined, { month: 'short' });
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthKeys.push(key);
      months.push(`${monthFormat(d)}`);
    }
    const netSeries = new Array(12).fill(0);
    (payDateGroups || []).forEach((group: any) => {
      if (!group.payDate) return;
      const d = new Date(group.payDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const idx = monthKeys.indexOf(key);
      if (idx >= 0) {
        netSeries[idx] += Number(group.amount || 0);
      }
    });
    // Simple moving average as trend line (window=3)
    const trendSeries = netSeries.map((_, i) => {
      const start = Math.max(0, i - 2);
      const slice = netSeries.slice(start, i + 1);
      const sum = slice.reduce((s, v) => s + v, 0);
      return slice.length ? sum / slice.length : 0;
    });
    this.trendChartData = {
      labels: months,
      datasets: [
        { label: 'Net amount', data: netSeries, borderColor: '#5b8def', backgroundColor: 'rgba(91,141,239,0.15)', fill: true, tension: 0.35, pointRadius: 2 },
        { label: 'Trend', data: trendSeries, borderColor: '#7c3aed', backgroundColor: 'rgba(124,58,237,0.0)', fill: false, borderDash: [6, 4], tension: 0.15, pointRadius: 0 }
      ]
    };

    // Donut breakdown (lifetime commissions vs adjustments) - using pay history data
    const totalAdjustmentsPaid = payDateGroups.reduce((sum: number, group: any) =>
      sum + (group.sales || []).filter((s: any) => s?.pay_type === 'commission_adjustment').reduce((s2: number, s: any) => s2 + Number(s.amount || 0), 0), 0);
    const totalCommissionsPaid = Math.max(0, this.lifetimeNet - totalAdjustmentsPaid);
    this.donutChartData = {
      labels: ['Commissions', 'Adjustments'],
      datasets: [{ data: [totalCommissionsPaid, totalAdjustmentsPaid], backgroundColor: ['#5b8def', '#f59e0b'] }]
    };

    // Upcoming adjustments bar chart by month
    const upMonths: string[] = [];
    const upKeys: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      upKeys.push(key);
      upMonths.push(d.toLocaleString(undefined, { month: 'short' }));
    }
    const upSeries = new Array(6).fill(0);
    (this.upcoming || []).forEach((u: any) => {
      if (!u?.payoutDate) return;
      const d = new Date(u.payoutDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const idx = upKeys.indexOf(key);
      if (idx >= 0) upSeries[idx] += Number(u.amount || 0);
    });
    this.upcomingChartData = { labels: upMonths, datasets: [{ data: upSeries, backgroundColor: '#22c55e' }] };
  }

  buildMotivation(): void {
    const dir = this.lastPeriodChangePct == null ? 0 : (this.lastPeriodChangePct > 0 ? 1 : (this.lastPeriodChangePct < 0 ? -1 : 0));
    if (dir > 0) {
      this.motivationalMessage = `Fantastic momentum — your net is up ${this.lastPeriodChangePct?.toFixed(1)}% vs last period.`;
    } else if (dir < 0) {
      this.motivationalMessage = `You’re setting up the comeback — focus on active selling time and work the close.`;
    } else {
      this.motivationalMessage = `Steady progress — you got this!`;
    }
  }

  onDateRangeChange(range: any): void {
    this.dateRange = range || [];
    this.applyFilters();
    this.cdr.markForCheck();
  }

  onSearchTextChange(): void {
    this.applyFilters();
    this.cdr.markForCheck();
  }

  exportCSV(): void {
    const rows: string[] = [];
    rows.push(['Pay Date', 'Cycle Net Amount', 'Line Type', 'Customer', 'Rule', 'Line Amount'].join(','));
    (this.visiblePayHistory || []).forEach((cycle: any) => {
      const payDate = cycle.payDate ? new Date(cycle.payDate).toISOString().slice(0, 10) : '';
      if (!cycle.sales || cycle.sales.length === 0) {
        rows.push([payDate, Number(cycle.amount || 0).toFixed(2), 'Summary', '', '', ''].join(','));
      } else {
        (cycle.sales || []).forEach((s: any) => {
          const customer = this.formatCustomerName(s);
          const rule = (s.rule_description || '').replace(/\n|\r|,/g, ' ');
          rows.push([payDate, Number(cycle.amount || 0).toFixed(2), s.is_adjustment ? 'Adjustment' : 'Commission', customer, rule, Number(s.amount || 0).toFixed(2)].join(','));
        });
      }
    });
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pay-history.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  formatCustomerName(s: any): string {
    if (s?.is_adjustment) return '';
    const first = (s?.first_name || '').trim();
    const last = (s?.last_name || '').trim();
    const fullName = (first && last) ? `${first} ${last}` : (first || last);
    if (fullName) return fullName;
    const orderId = s?.workOrderId || s?.work_order_id || s?.crm_id || s?.order_id || s?.orderId || '';
    return orderId ? `WO ${orderId}` : 'Unlabeled line';
  }

  // New helper methods for the enhanced UI

  getStatusColor(status: string): string {
    const normalized = (status || '').toLowerCase();
    switch (normalized) {
      case 'paid':
      case 'released':
      case 'approved':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  }

  // Mobile card expansion methods
  toggleCycleCardExpansion(cycleId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.expandedCycleCards.has(cycleId)) {
      this.expandedCycleCards.delete(cycleId);
    } else {
      this.expandedCycleCards.add(cycleId);
    }
    this.cdr.markForCheck();
  }

  isCycleCardExpanded(cycleId: string): boolean {
    return this.expandedCycleCards.has(cycleId);
  }

  toggleCustomerCardExpansion(customerId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.expandedCustomerCards.has(customerId)) {
      this.expandedCustomerCards.delete(customerId);
    } else {
      this.expandedCustomerCards.add(customerId);
    }
    this.cdr.markForCheck();
  }

  isCustomerCardExpanded(customerId: string): boolean {
    return this.expandedCustomerCards.has(customerId);
  }

  viewCustomerPerformanceDeets(customer: any) {
    this.customerPerformanceModal = true;
    this.selectedCustomerPerformance = customer;
    this.selectedCustomerAddressLines = this.buildCustomerAddressLines(customer);
  }

  handleCancel() {
    this.customerPerformanceModal = false;
    this.selectedCustomerPerformance = null;
    this.selectedCustomerAddressLines = [];
  }

  prettyColumn(column: any) {
    let c = column.toLowerCase();
    if (c == 'fiber') {
      return 'Base';
    } else if (c == 'hb') {
      return 'Holdback'
    } else if (c == 'whole home wifi') {
      return 'Wifi'
    } else {
      return c;
    }
  }

  private setLoadingState(spinning: boolean, tip?: string): void {
    if (this.loadingTipTimeout) {
      clearTimeout(this.loadingTipTimeout);
      this.loadingTipTimeout = null;
    }

    if (spinning) {
      this.loading = true;
      this.verbiage = tip || 'Loading...';
      this.loadingTipTimeout = setTimeout(() => {
        if (!this.loading) return;
        this.verbiage = 'Still working on it...';
        this.cdr.markForCheck();
      }, 6000);
    } else {
      this.loading = false;
      this.verbiage = '';
    }

    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.loadingTipTimeout) {
      clearTimeout(this.loadingTipTimeout);
    }
  }

  private buildCustomerAddressLines(customer: any): string[] {
    if (!customer) {
      return [];
    }

    const addressLines: string[] = [];
    const line1 = this.getAddressField(customer, ['addressLine1', 'address_line1', 'address1', 'line1']);
    const line2 = this.getAddressField(customer, ['addressLine2', 'address_line2', 'address2', 'line2']);
    const city = this.getAddressField(customer, ['city', 'cityName']);
    const state = this.getAddressField(customer, ['state', 'stateCode']);
    const postal = this.getAddressField(customer, ['zip', 'zipCode', 'postalCode', 'postal_code']);

    if (line1) {
      addressLines.push(line1 + ', ');
    }
    if (line2) {
      addressLines.push(line2 + ', ');
    }

    const cityState = [city, state].filter(Boolean).join(', ');
    const finalLine = [cityState, postal].filter(Boolean).join(' ').trim();
    if (finalLine) {
      addressLines.push(finalLine);
    }

    return addressLines;
  }

  private getAddressField(customer: any, keys: string[]): string {
    for (const key of keys) {
      const raw = customer?.[key];
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed) {
          return trimmed;
        }
      }
    }
    return '';
  }

  exportToCSV() {
    if (!this.selectedCycleId) {
      this.message.warning('Please select a payroll cycle first');
      return;
    }

    console.log('Exporting detailed cycle data for cycle:', this.selectedCycleId);


    // Find the selected cycle to get the actual cycle IDs (use grouped data, not the flattened payHistory)
    const selectedCycle = this.allGroupedPayHistory.find((c: any) => c.id === this.selectedCycleId);
    if (!selectedCycle) {
      this.message.error('Unable to find cycle details for export');
      return;
    }

    // Extract actual cycle IDs from the cycle data
    const actualCycleIds = Array.isArray(selectedCycle.cycles) && selectedCycle.cycles.length
      ? selectedCycle.cycles.map((cycle: any) => cycle.id)
      : [selectedCycle.cycleId || selectedCycle.id];

    if (!actualCycleIds.length) {
      this.message.error('Unable to find cycle details for export');
      return;
    }
    console.log('Using actual cycle IDs for export:', actualCycleIds);

    // Get detailed export data from backend
    this.dataService.getCycleCustomerDetails(actualCycleIds, this.selectedUserId || this.currentUser.id)
      .subscribe({
        next: (detailedData) => {
          console.log('Received detailed export data:', detailedData);

          const csvData = [];

          csvData.push([
            'Payee', 'Original Rep Name', 'Account Number', 'Order Id', 'Customer Name',
            'State', 'Product', 'Plan', 'Sale Date', 'Activation Date', 'Commission Amount', 'Holdback',
            'Adjustment', 'Net Pay', 'Note', 'Pay Cycle Date'
          ]);

          detailedData.forEach((record: any) => {
            csvData.push([
              record.payee || this.currentUser.first_name + ' ' + this.currentUser.last_name,
              record.originalSalesRep || 'N/A',
              record.account_number || 'N/A',
              record.order_id || 'N/A',
              record.customerName || 'N/A',
              record.state || 'N/A',
              record.product || 'N/A',
              record.plan || 'N/A',
              record.sales_date ? new Date(record.sales_date).toLocaleDateString() : 'N/A',
              record.activationDate ? new Date(record.activationDate).toLocaleDateString() : 'N/A',
              record.totalCommissions || 0,
              record.totalHoldbacks || 0,
              record.totalAdjustments || 0,
              record.netPay || 0,
              record.note || 'N/A',
              record.pay_date ? new Date(record.pay_date).toLocaleDateString() : 'N/A'
            ]);
          });

          // Prefer the grouped record we already have; fall back to payHistory if needed
          const selectedCycleForFilename = selectedCycle || this.payHistory.find((c: any) => c.id === this.selectedCycleId);
          const cycleDate = selectedCycleForFilename?.payDate
            ? new Date(selectedCycleForFilename.payDate).toISOString().split('T')[0]
            : 'unknown';
          const companyName = this.currentUser?.companyInfo?.name || this.currentUser?.company || 'company';

          this.downloadCSV(csvData, `${companyName}-detailed-cycle-${cycleDate}.csv`);
        },
        error: (error) => {
          console.error('Failed to get detailed export data:', error);
          this.message.error('Failed to export detailed cycle data');
        }
      });
  }

  private downloadCSV(data: any[][], filename: string) {
    const csvContent = data.map(row =>
      row.map(field =>
        typeof field === 'string' && field.includes(',')
          ? `"${field}"`
          : field
      ).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  formatPayDate(date: any, format: string = 'mediumDate'): string {
    if (!date) return '';
    // Parse ISO date string directly to avoid timezone issues
    const dateStr = String(date);
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      if (format === 'MMM d, yyyy') {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthName = monthNames[parseInt(month) - 1];
        return `${monthName} ${parseInt(day)}, ${year}`;
      }
      return `${month}/${day}/${year}`;
    }
    // Use UTC methods to avoid timezone conversion
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    if (format === 'MMM d, yyyy') {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[d.getUTCMonth()];
      return `${monthName} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
    }
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${month}/${day}/${year}`;
  }
}
